import { useMemo } from 'react';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { queryFnFetchErrorWrapper } from '../utils/errorHandlers';
import { FetchError } from 'src/types/TApiQueries';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { saveChatSession, deleteChatSessionFromBackend, rollbackChatOp } from 'src/api/functions/chatApi';
import { queryNames } from 'src/api/utils/queryNames';
import type {
  RollbackHttpResult,
  SaveSessionPayload,
  SaveSessionResponse,
} from 'src/chat/types';

export function useSaveChatSessionMutation(
  mutationOptions?: UseMutationOptions<SaveSessionResponse, FetchError, SaveSessionPayload>,
) {
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((payload: SaveSessionPayload) => saveChatSession(accessToken, payload)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries([queryNames.chatSessions]);
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export interface RollbackChatOpVariables {
  opId: string;
  sessionId?: string;
}

// Note: rollbackChatOp returns a discriminated `RollbackHttpResult` instead of
// throwing — the caller branches on `result.ok` for success vs error UX.
export function useRollbackChatOpMutation(
  mutationOptions?: UseMutationOptions<RollbackHttpResult, FetchError, RollbackChatOpVariables>,
) {
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => (vars: RollbackChatOpVariables) =>
      rollbackChatOp({ opId: vars.opId, sessionId: vars.sessionId, accessToken }),
    [accessToken],
  );

  return useMutation(mutationFn, mutationOptions);
}

export function useDeleteChatSessionMutation(
  mutationOptions?: UseMutationOptions<void, FetchError, string>,
) {
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((sessionId: string) => deleteChatSessionFromBackend(accessToken, sessionId)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries([queryNames.chatSessions]);
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}
